import React, { useEffect, useRef, useState } from "react";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { connect, useDispatch, useSelector } from "react-redux";
import { useHistory, withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { clearAllNotes, setShowNotesList } from "../../../../actions/notes";
import { setAllUserDetails } from "../../../../actions/userDetails";
import { updateUserAvailabilityStatus } from "../../../../actions/userProfile";
import { setShowTeamSettingSidebar } from "../../../../actions/teamSettingSidebar";
import { setShowUserProfileSidebar } from "../../../../actions/userProfileSidebar";
import Notes from "../../../../components/Notes/Notes";
import { getNotes } from "../../../../components/Notes/notes.service";
import { PROJECT_TYPE } from "../../../../components/ProjectSidebar/projectSidebar.constants";
import { handleCloseSidebar } from "../../../../components/Tasks/tasks.common";
import { button, icon, label, route } from "../../../../config";
import { number } from "../../../../config/constants";
import notes from "../../../../reducers/notes";
import { logoutUser } from "../../../../shared/services/api.service";
import { subStr } from "../../../../shared/validators/validator";
import { checkAdmin } from "../../../../utils";
import { checkSuperAdmin } from "../../../../utils/common";
import { formatUserStatus } from "../../../../utils/statusFormatter";
import { openProjectSidebar } from "../../../services/projects.service";
import { loadState, saveState } from "../../../services/session.service";
import ChangePassword from "../ChangePassword/ChangePassword";
import SetAwayStatusModal from "./SetAwayStatusModal";
import "./UserDropdown.scss";

/**
 * @author Himanshi Chawla
 */

const UserDropdown = (props) => {
  const { toggleDropdown, openProfile, buttonRef } = props;
  const [showCompanyName, setShowCompanyName] = useState(null);
  const [showUserName, setShowUserName] = useState(null);
  const userDropdown = useRef(null);
  const changePassword = useRef(null);
  const { switcher, currentTheme, themes } = useThemeSwitcher();
  let theme = loadState("mode").length ? loadState("mode") : "light";
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const history = useHistory();
  const company = useSelector((state) => state.company);
  const [notesLoading, setNotesLoading] = useState(false);
  const notes = useSelector((state) => state.notes);
  const [isDarkMode, setIsDarkMode] = useState();
  const [showAwayModal, setShowAwayModal] = useState(false);
  const awayModalAnchorRef = useRef(null);

  const user = auth.user;
  const organization = company.organization;

  useEffect(() => {
    setShowCompanyName(subStr(user.displayName, number.ZERO, number.NINETEEN));
    setShowUserName(subStr(user.Name, number.ZERO, number.NINETEEN));
  }, [user.id]);

  /**
   * updates theme switcher
   * on toggling themes
   */
  React.useEffect(() => {
    switcher({ theme });
  }, [theme]);

  /**
   * handles theme mode of drutas
   */
  React.useEffect(() => {
    if (currentTheme == "light") {
      setIsDarkMode(false);
      import("../../../../assets/themes/light-theme-variables.scss");
      import("../../../../assets/themes/light-theme.scss");
    } else if (currentTheme == "dark") {
      setIsDarkMode(true);
      import("../../../../assets/themes/dark-theme-variables.scss");
      import("../../../../assets/themes/dark-theme.scss");
    }
  }, [currentTheme]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdown.current && !userDropdown.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
        toggleDropdown();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  /**
   * updates the breadcrumbs for manage organisation
   * if user propagated through superadmin console
   * @param {void}
   * @returns {void}
   * @author Pragun Gandotra
   */
  const handleClickAdminConsole = () => {
    const adminRoute = route.ADMIN_ROUTE.MANAGE_USERS;
    if (adminRoute.PARENT_ROUTES.length > number.ONE) {
      adminRoute.PARENT_ROUTES.pop();
      adminRoute.PROPS.HEADER = "Manage Users";
    }
    history.push(adminRoute.PATH);
    props.toggleDropdown();
    handleCloseSidebar(dispatch, { isNewTask: false });
  };

  /**
   * logout of app
   */
  const logOut = () => {
    dispatch(logoutUser());
  };

  /**
   * handling the open/close of NOotes dropdown and getNotes api call
   * @returns {void}
   *
   */
  const handleNotesButtonClick = async () => {
    toggleDropdown();
    setNotesLoading(true);
    await dispatch(setShowNotesList(!notes.showNotesList));
    await fetchNotes();
    setNotesLoading(false);
  };

  const fetchNotes = async () => {
    await dispatch(clearAllNotes());
    const payload = { userId: user.id, limit: number.FIFTEEN, offset: number.ZERO };
    await dispatch(getNotes(payload));
  };

  const handleLightMode = () => {
    setIsDarkMode(false);
    switcher({ theme: themes.light });
    saveState("mode", "light");
    toggleDropdown();
  };

  const handleDarkMode = () => {
    setIsDarkMode(true);
    switcher({ theme: themes.dark });
    saveState("mode", "dark");
    toggleDropdown();
  };

  const currentStatus = user?.availabilityStatus || label.AVAILABLE;
  const customStatus = user?.customStatus || null;
  const normalizedStatus = currentStatus?.toString().trim();
  const isAvailable = normalizedStatus === label.AVAILABLE;
  const statusInfo = formatUserStatus(currentStatus, customStatus, 20);
  const formattedStatus = statusInfo.display;
  const fullStatus = statusInfo.full;

  const getUserName = () => {
    return user.Name && user.Name.trim().length >= number.NINETEEN ? showUserName : user.Name;
  };

  const userName = getUserName();

  return (
    <div
      id='user-dropdown-on-click'
      ref={userDropdown}
      className={`${openProfile ? `d-block` : `d-none`} header-dropdown-menu position-absolute profile-dropdown-menu right-0`}>
      <ul className='project-setting-list mt-0 pl-0 cursor-pointer'>
        <li
          className='d-inline-block w-100 cursor-pointer userdropdown-name'
          onClick={async () => {
            await handleCloseSidebar(dispatch, { isNewTask: false });
            dispatch(setAllUserDetails({ visitingByAdmin: false, details: null, allProjects: [] }));
            dispatch(setShowTeamSettingSidebar({ showTeamSettingSidebar: false }));
            dispatch(setShowUserProfileSidebar({ showUserProfileSidebar: true }));
            props.toggleDropdown();
          }}>
          <div className='d-flex flex-column align-items-start'>
            <span className='userdropdown-name-text'>{userName}</span>
            {formattedStatus ? (
              <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                <span className='userdropdown-status-text' title={fullStatus}>
                  {formattedStatus}
                </span>
              </Tooltip>
            ) : (
              <span className='userdropdown-status-text'>{statusInfo?.display || `(${currentStatus})`}</span>
            )}
          </div>
        </li>

        <li className='d-inline-block w-100 cursor-pointer'>
          <span ref={awayModalAnchorRef} className='cursor-pointer d-flex align-items-center justify-content-between'>
            <span
              className='d-flex align-items-center flex-grow-1'
              onClick={async (e) => {
                e.stopPropagation();

                const newStatus = isAvailable ? label.AWAY : label.AVAILABLE;
                await dispatch(updateUserAvailabilityStatus(user.id, newStatus));
              }}>
              <span className='mr-2'>{icon.USER_PEN}</span>
              <span>{isAvailable ? `Mark as ${label.AWAY}` : `Mark as ${label.AVAILABLE}`}</span>
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation();
                setShowAwayModal(true);
              }}>
              {icon.CHEVRON_DOWN}
            </span>
          </span>
        </li>

        <li className='d-inline-block w-100 cursor-pointer'>
          <span id='user-dropdown-notes' className='cursor-pointer' disabled={notesLoading} onClick={handleNotesButtonClick}>
            {icon.EDIT_NOTES} <span className='ml-2'>Notes</span>
          </span>
        </li>

        {checkAdmin(user.isAdmin) && (
          <li className='d-inline-block w-100 cursor-pointer'>
            <span id='user-dropdown-admin-console' className='cursor-pointer' onClick={handleClickAdminConsole}>
              {icon.ADMIN_SETTINGS}
              <span>{button.ADMIN_CONSOLE}</span>
            </span>
          </li>
        )}
        {checkSuperAdmin(user.IsSuperAdmin) && (
          <li className='d-inline-block w-100 cursor-pointer'>
            <Link
              to={route.SUPER_ADMIN_ROUTE.VIEW_ORGANISATION.PATH}
              className='text-decoration-none text-reset'
              onClick={() => {
                props.toggleDropdown();
                handleCloseSidebar(dispatch, { isNewTask: false });
              }}>
              <span id='user-dropdown-super-admin-console'>
                {icon.ADMIN_SETTINGS}
                <span>{button.SUPER_ADMIN_CONSOLE}</span>
              </span>
            </Link>
          </li>
        )}
        <li className='bg-transparent cursor-default pb-0'>Theme</li>
        <div className='cursor-pointer theme-container white-color d-flex'>
          <button
            className={`w-50 ${
              isDarkMode ? "de-activate" : "activate-light-mode "
            } container-class cursor-pointer border-none d-flex align-items-center justify-content-center`}
            onClick={handleLightMode}>
            {icon.LIGHT_MODE} Light
          </button>

          <button
            className={`w-50 ${
              isDarkMode ? "badge-dark" : " de-activate "
            } container-class cursor-pointer border-none d-flex align-items-center justify-content-center`}
            onClick={handleDarkMode}>
            {icon.DARK_MODE} Dark
          </button>
        </div>
        <div className='dropdown-divider mb-0 opacity-1 overflow-hidden'></div>
        <li className='d-inline-block w-100 cursor-pointer'>
          <span onClick={logOut} id='log-out'>
            {icon.LOGOUT} {button.LOGOUT}
          </span>
        </li>
      </ul>
      <Notes />
      <SetAwayStatusModal show={showAwayModal} onClose={() => setShowAwayModal(false)} anchor={awayModalAnchorRef} />
    </div>
  );
};

export default UserDropdown;
